import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';

const getBackendUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? 'http://localhost:8000' : 'https://gradi.ai';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const VideoDetails = ({ videoId, onBack }) => {
  const [videoData, setVideoData] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (videoId) {
      fetchVideoDetails();
      // Automatically trigger analysis
      triggerAnalysis();
      setChatQuestion('');
      setChatMessages([]);
      setChatError(null);
    }
  }, [videoId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const fetchVideoDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch video stats from backend
      const response = await fetch(`${API}/video-stats?video_id=${videoId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVideoData(data.data);
          setIsLoading(false);
          return;
        }
      }

      throw new Error('Could not fetch video data');
    } catch (err) {
      setError(err.message || 'Failed to load video details');
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const triggerAnalysis = async () => {
    if (!videoId) return;
    
    setIsAnalyzing(true);
    try {
      const url = `https://www.youtube.com/watch?v=${videoId}`;
      const response = await apiService.analyzeVideo(url, 'english', null);
      if (response.success) {
        setAnalysisResult(response.data);
      }
    } catch (err) {
      console.error('Analysis error:', err);
      // Don't show error to user, just log it
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleChatSubmit = async (event) => {
    event.preventDefault();
    const trimmedQuestion = chatQuestion.trim();
    if (!trimmedQuestion || !videoId || !analysisResult) {
      return;
    }

    const historyPayload = chatMessages.map((msg) => ({
      role: msg.role,
      content: msg.content
    })).slice(-6);

    const userMessage = {
      role: 'user',
      content: trimmedQuestion,
      timestamp: new Date().toISOString()
    };

    setChatMessages((prev) => {
      const updated = [...prev, userMessage];
      return updated.length > 12 ? updated.slice(updated.length - 12) : updated;
    });
    setChatQuestion('');
    setChatLoading(true);
    setChatError(null);

    try {
      const response = await apiService.askTranscriptQuestion(
        videoId,
        trimmedQuestion,
        analysisResult?.analysis_language || 'hinglish',
        historyPayload
      );

      if (response?.success && response.answer) {
        const assistantMessage = {
          role: 'assistant',
          content: response.answer,
          source: response.source,
          timestamp: new Date().toISOString()
        };
        setChatMessages((prev) => {
          const updated = [...prev, assistantMessage];
          return updated.length > 12 ? updated.slice(updated.length - 12) : updated;
        });
      } else {
        setChatError(response?.error || 'Could not get an answer right now.');
      }
    } catch (err) {
      setChatError(err.message || 'Could not get an answer right now.');
    } finally {
      setChatLoading(false);
    }
  };

  const calculateEstimatedEarnings = (views) => {
    // Conservative estimate: $0.5 - $2 per 1000 views for educational content
    // Convert to INR (1 USD ≈ 83 INR)
    const monthlyViews = views * 0.01; // Assume ~1% monthly views
    const lowUSD = (monthlyViews / 1000) * 0.5;
    const highUSD = (monthlyViews / 1000) * 2.0;
    const lowINR = Math.round(lowUSD * 83);
    const highINR = Math.round(highUSD * 83);
    return { low: lowINR, high: highINR };
  };

  // Generate views performance graph data
  const generateViewsGraphData = () => {
    if (!videoData || !videoData.published_at || !videoData.views) return null;

    const publishedDate = new Date(videoData.published_at);
    const now = new Date();
    const daysSincePublished = Math.floor((now - publishedDate) / (1000 * 60 * 60 * 24));
    
    if (daysSincePublished < 1) return null;

    const totalViews = videoData.views || 0;
    const dataPoints = Math.min(30, daysSincePublished); // Max 30 data points
    const viewsData = [];

    // Simulate views growth over time (exponential decay model)
    for (let i = 0; i <= dataPoints; i++) {
      const daysAgo = Math.floor((dataPoints - i) * (daysSincePublished / dataPoints));
      // Simulate views: more views in first days, then slower growth
      const progress = i / dataPoints;
      const simulatedViews = Math.floor(totalViews * (0.1 + 0.9 * Math.pow(progress, 0.7)));
      
      viewsData.push({
        label: daysAgo > 30 ? `${Math.floor(daysAgo / 30)}M` : `${daysAgo}d`,
        views: simulatedViews,
        index: i
      });
    }

    return {
      viewsData,
      maxViews: totalViews,
      avgViews: totalViews / (dataPoints + 1)
    };
  };

  // Render views performance graph
  const renderViewsGraph = (data) => {
    if (!data || data.viewsData.length === 0) return null;

    const chartWidth = 800;
    const chartHeight = 300;
    const padding = { top: 20, right: 30, bottom: 40, left: 60 };
    const graphWidth = chartWidth - padding.left - padding.right;
    const graphHeight = chartHeight - padding.top - padding.bottom;

    const maxValue = data.maxViews > 0 ? data.maxViews : 1;
    const scaleY = graphHeight / maxValue;
    const stepX = graphWidth / (data.viewsData.length - 1);

    const points = data.viewsData.map((d, i) => {
      const x = padding.left + (i * stepX);
      const y = padding.top + graphHeight - (d.views * scaleY);
      return `${x},${y}`;
    }).join(' ');

    const areaPath = `M${padding.left},${padding.top + graphHeight} L${points} L${padding.left + graphWidth},${padding.top + graphHeight} Z`;
    const gradientId = `viewsGradient-${Math.random().toString(36).substring(2, 9)}`;

    return (
      <div className="step-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', marginBottom: '1rem', color: '#e5e7eb', textAlign: 'center' }}>
          Views Performance Over Time
        </h3>
        <div style={{ width: '100%', overflowX: 'auto', paddingBottom: '1rem' }}>
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ width: '100%', height: 'auto', maxWidth: '800px', margin: '0 auto', display: 'block' }}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.7"/>
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/>
              </linearGradient>
            </defs>

            {/* Y-axis grid lines and labels */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
              const y = padding.top + graphHeight - (ratio * graphHeight);
              return (
                <g key={`y-axis-${i}`}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke="#333"
                    strokeDasharray="2 2"
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left - 10}
                    y={y + 5}
                    fill="#9ca3af"
                    fontSize="12"
                    textAnchor="end"
                  >
                    {formatNumber(ratio * maxValue)}
                  </text>
                </g>
              );
            })}

            {/* Area fill */}
            <path
              d={areaPath}
              fill={`url(#${gradientId})`}
              opacity={0.3}
            />

            {/* Line */}
            <path
              d={points}
              fill="none"
              stroke="#8b5cf6"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Data points */}
            {data.viewsData.map((d, i) => {
              const x = padding.left + (i * stepX);
              const y = padding.top + graphHeight - (d.views * scaleY);
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    fill="#8b5cf6"
                    stroke="#fff"
                    strokeWidth="2"
                  />
                  {/* X-axis labels */}
                  {i % Math.ceil(data.viewsData.length / 5) === 0 && (
                    <text
                      x={x}
                      y={chartHeight - padding.bottom + 20}
                      fill="#9ca3af"
                      fontSize="11"
                      textAnchor="middle"
                    >
                      {d.label}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899']; // Consistent color scheme
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 2)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  if (isLoading || isAnalyzing) {
    return (
      <div className="page-container">
        <div className="animated-background" />
        <div className="floating-particles">{generateParticles()}</div>
        <div className="page-content" style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <div className="spinner" />
          <p style={{ color: '#fff', marginTop: '1rem' }}>
            {isAnalyzing ? 'Analyzing video...' : 'Loading video details...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !videoData) {
    return (
      <div className="page-container">
        <div className="animated-background" />
        <div className="floating-particles">{generateParticles()}</div>
        <div className="page-content" style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <div className="error-container">
            <p className="error-content">{error || 'Video not found'}</p>
            {onBack && (
              <button onClick={onBack} className="analyze-button" style={{ marginTop: '1rem' }}>
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const earnings = calculateEstimatedEarnings(videoData.views || 0);

  return (
    <div className="page-container">
      <div className="animated-background" />
      <div className="floating-particles">{generateParticles()}</div>

      <div className="page-content">
        {onBack && (
          <button
            onClick={onBack}
            style={{
              marginBottom: '2rem',
              background: 'rgba(139, 92, 246, 0.2)',
              border: '2px solid rgba(139, 92, 246, 0.5)',
              color: '#fff',
              padding: '0.75rem 1.5rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.4)';
              e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.8)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
            }}
          >
            ← Back
          </button>
        )}

        <div className="results-container">
          {/* Video Player Section */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              background: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '2px solid rgba(139, 92, 246, 0.3)'
            }}>
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=0`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              />
            </div>
          </div>

          {/* Video Info Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="hero-title" style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '1rem'
            }}>
              {videoData.title || 'Untitled Video'}
            </h1>
            
            {videoData.published_at && (
              <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                {formatDate(videoData.published_at)}
              </p>
            )}

            {videoData.channel_title && (
              <p style={{ color: '#9ca3af', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                Channel: {videoData.channel_title}
              </p>
            )}
          </div>

          {/* Statistics Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '900', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                {formatNumber(videoData.views || 0)}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Views</div>
            </div>

            <div style={{
              background: 'rgba(236, 72, 153, 0.1)',
              border: '2px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '16px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '900', color: '#ec4899', marginBottom: '0.5rem' }}>
                {formatNumber(videoData.likes || 0)}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Likes</div>
            </div>

            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '900', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                {formatNumber(videoData.comments || 0)}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Comments</div>
            </div>

            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6', marginBottom: '0.5rem' }}>
                ₹{formatNumber(earnings.low)} - ₹{formatNumber(earnings.high)}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Est. Earnings (Monthly)</div>
            </div>

            {analysisResult?.analysis?.category && (
              <div style={{
                background: 'rgba(236, 72, 153, 0.1)',
                border: '2px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ec4899', marginBottom: '0.5rem' }}>
                  {analysisResult.analysis.category}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Category</div>
              </div>
            )}
          </div>

          {/* Views Performance Graph */}
          {(() => {
            const graphData = generateViewsGraphData();
            return graphData && renderViewsGraph(graphData);
          })()}

          {(analysisResult || chatMessages.length > 0 || isAnalyzing) && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.5rem',
              justifyContent: 'center',
              marginTop: '2rem'
            }}>
              {analysisResult?.analysis && (
                <div style={{
                  flex: '1 1 360px',
                  maxWidth: '540px',
                  background: 'rgba(139, 92, 246, 0.05)',
                  border: '2px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '16px',
                  padding: '1.5rem'
                }}>
                  <h3 className="hero-title" style={{
                    fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                    marginBottom: '1rem'
                  }}>
                    Gradi Analysis
                  </h3>
                  {(() => {
                    const rawScore = analysisResult?.analysis?.gradi_score;
                    let scoreValue = null;
                    if (typeof rawScore === 'number') {
                      scoreValue = rawScore;
                    } else if (typeof rawScore === 'string') {
                      const parsed = parseFloat(rawScore);
                      if (!Number.isNaN(parsed)) {
                        scoreValue = parsed;
                      }
                    } else if (rawScore && typeof rawScore === 'object') {
                      const possible = rawScore.score ?? rawScore.value;
                      if (typeof possible === 'number') {
                        scoreValue = possible;
                      } else if (typeof possible === 'string') {
                        const parsed = parseFloat(possible);
                        if (!Number.isNaN(parsed)) {
                          scoreValue = parsed;
                        }
                      }
                    }
                    if (scoreValue === null || Number.isNaN(scoreValue)) {
                      return null;
                    }
                    return (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Gradi Score:</span>
                          <span style={{ color: '#8b5cf6', fontSize: '1.5rem', fontWeight: '700' }}>
                            {scoreValue.toFixed(1)}/5.0
                          </span>
                        </div>
                        {rawScore && typeof rawScore === 'object' && rawScore.reason && (
                          <p style={{ color: '#9ca3af', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                            {rawScore.reason}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  {analysisResult.analysis.summary && (
                    <p style={{
                      color: '#d4d4d4',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {analysisResult.analysis.summary}
                    </p>
                  )}
                </div>
              )}

              <div style={{
                flex: '1 1 360px',
                maxWidth: '540px',
                background: 'rgba(55, 65, 81, 0.35)',
                border: '2px solid rgba(139, 92, 246, 0.25)',
                borderRadius: '16px',
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div>
                  <h3 className="hero-title" style={{
                    fontSize: 'clamp(1.05rem, 2.3vw, 1.3rem)',
                    marginBottom: '0.35rem'
                  }}>
                    Chat With This Video
                  </h3>
                  <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                    Ask Gradi about any concept, example, or takeaway mentioned in this video. Answers come straight from the instructor’s explanation.
                  </p>
                </div>

                {chatMessages.length > 0 && (
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.55)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '12px',
                    padding: '1rem',
                    maxHeight: '260px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                    {chatMessages.map((message, index) => (
                      <div
                        key={`${message.timestamp || index}-${index}`}
                        style={{
                          display: 'flex',
                          justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <div style={{
                          maxWidth: '85%',
                          background: message.role === 'user'
                            ? 'linear-gradient(135deg, rgba(139,92,246,0.8), rgba(236,72,153,0.8))'
                            : 'rgba(55, 65, 81, 0.8)',
                          borderRadius: message.role === 'user' ? '14px 14px 0 14px' : '14px 14px 14px 0',
                          padding: '0.75rem 1rem',
                          color: '#f9fafb',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.25)'
                        }}>
                          {message.content}
                          {message.role === 'assistant' && message.source && (
                            <div style={{
                              marginTop: '0.5rem',
                              fontSize: '0.75rem',
                              opacity: 0.65
                            }}>
                              {message.source === 'transcript' ? 'Based on the video content' : 'Powered by Gemini 2.5 Flash Lite'}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <div style={{
                          background: 'rgba(55, 65, 81, 0.8)',
                          borderRadius: '14px 14px 14px 0',
                          padding: '0.75rem 1rem',
                          color: '#f9fafb',
                          fontSize: '0.9rem'
                        }}>
                          Thinking...
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}

                {chatMessages.length === 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {['Main takeaway?', 'Explain the key example.', 'Any actionable advice?'].map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() => setChatQuestion(prompt)}
                        style={{
                          background: 'rgba(139, 92, 246, 0.15)',
                          border: '1px solid rgba(139, 92, 246, 0.35)',
                          color: '#d1d5db',
                          padding: '0.35rem 0.75rem',
                          borderRadius: '999px',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.25)';
                          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.55)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.35)';
                        }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}

                <form onSubmit={handleChatSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <textarea
                    value={chatQuestion}
                    onChange={(e) => setChatQuestion(e.target.value)}
                    placeholder={analysisResult ? 'Ask about any detail from the video...' : 'Hang tight, analysis is still running.'}
                    rows={3}
                    style={{
                      width: '100%',
                      background: 'rgba(17, 24, 39, 0.6)',
                      border: '1px solid rgba(139, 92, 246, 0.35)',
                      borderRadius: '12px',
                      padding: '0.75rem 1rem',
                      color: '#e5e7eb',
                      fontSize: '0.95rem',
                      resize: 'vertical',
                      minHeight: '90px',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.6)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.35)'}
                    disabled={chatLoading || !analysisResult}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                      type="submit"
                      disabled={chatLoading || !chatQuestion.trim() || !analysisResult}
                      style={{
                        background: chatLoading || !chatQuestion.trim() || !analysisResult
                          ? 'rgba(139, 92, 246, 0.25)'
                          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.85), rgba(236, 72, 153, 0.85))',
                        border: 'none',
                        color: '#fff',
                        padding: '0.6rem 1.2rem',
                        borderRadius: '999px',
                        cursor: chatLoading || !chatQuestion.trim() || !analysisResult ? 'not-allowed' : 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        fontSize: '0.95rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseOver={(e) => {
                        if (!chatLoading && chatQuestion.trim() && analysisResult) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 6px 25px rgba(139, 92, 246, 0.35)';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {chatLoading ? 'Thinking...' : 'Ask Gradi'}
                    </button>
                    <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      Responses powered by the video transcript.
                    </span>
                  </div>
                </form>
                {!analysisResult && (
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                    We’re analyzing the video. Chat unlocks automatically once the summary is ready.
                  </p>
                )}
                {chatError && (
                  <p style={{ color: '#f87171', fontSize: '0.85rem' }}>
                    {chatError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {videoData.description && (
            <div style={{
              background: 'rgba(139, 92, 246, 0.05)',
              border: '2px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginTop: '2rem'
            }}>
              <h3 className="hero-title" style={{
                fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                marginBottom: '1rem'
              }}>
                Description
              </h3>
              <p style={{
                color: '#d4d4d4',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {videoData.description.length > 500 
                  ? videoData.description.substring(0, 500) + '...' 
                  : videoData.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetails;

